import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { IPluginConfig } from '../../models/configuration/importExport/IPluginConfig';
import PluginService from '../../services/pluginService';
import { RootState } from '../store'; // Adjust the path as necessary
import EncompassService from '../../services/encompassService';

interface ImportPluginConfigState {
  config: IPluginConfig | null;
  loadingSave: boolean;
  loadingFetch: boolean;
  error: string | null;
}

const initialState: ImportPluginConfigState = {
  config: null,
  loadingSave: false,
  loadingFetch: false,
  error: null,
};

export const saveImportPluginConfig = createAsyncThunk(
  'importPluginConfig/saveImportPluginConfig',
  async ({ configurationId, config }: { configurationId: string; config: IPluginConfig }, { getState }) => {
    const state = getState() as RootState;
    const token = state.appSlice.accessToken;
    const pluginService = new PluginService();
    const requestBody = {
        "ConfigurationId": configurationId,
        "EncompassInstanceId": EncompassService.getEncompassInstanceId(),
        "Configuration": {
            ...config
        }
    }
    return await pluginService.saveEpcApiConfig(token, "plugin", configurationId, requestBody);
  }
);

export const fetchImportPluginConfig = createAsyncThunk<IPluginConfig, string, { rejectValue: string }>(
  'importPluginConfig/fetchImportPluginConfig',
  async (configurationId: string, { getState, rejectWithValue }): Promise<IPluginConfig | ReturnType<typeof rejectWithValue>> => {
    try {
      const state = getState() as RootState;
      const token = state.appSlice.accessToken;
      const pluginService = new PluginService();
      const response = await pluginService.getEpcApiConfig<IPluginConfig>(token, "plugin", configurationId);
      return response as IPluginConfig;
    } catch (error) {
      let errorMessage = 'Failed to fetch import plugin config';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

const importPluginConfigSlice = createSlice({
  name: 'importPluginConfig',
  initialState,
  reducers: {
    setImportPluginConfig(state, action: PayloadAction<IPluginConfig>) {
      state.config = action.payload;
    },
    clearImportPluginConfig(state) {
      state.config = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveImportPluginConfig.pending, (state) => {
        state.loadingSave = true;
        state.error = null;
      })
      .addCase(saveImportPluginConfig.fulfilled, (state, action: PayloadAction<{ ConfigurationId: string; EncompassInstanceId: string; Configuration: IPluginConfig }>) => {
        state.loadingSave = false;
        state.config = action.payload.Configuration;
      })
      .addCase(saveImportPluginConfig.rejected, (state, action) => {
        state.loadingSave = false;
        state.error = action.error.message || 'Failed to save import plugin config';
      })
      .addCase(fetchImportPluginConfig.pending, (state) => {
        state.loadingFetch = true;
        state.error = null;
      })
      .addCase(fetchImportPluginConfig.fulfilled, (state, action: PayloadAction<IPluginConfig>) => {
        state.loadingFetch = false;
        state.config = action.payload;
      })
      .addCase(fetchImportPluginConfig.rejected, (state, action) => {
        state.loadingFetch = false;
        state.error = action.error.message || 'Failed to fetch import plugin config';
      });
  },
});

export const { setImportPluginConfig, clearImportPluginConfig } = importPluginConfigSlice.actions;
export default importPluginConfigSlice.reducer;
